import { z, ZodType } from 'zod';
import { zSchema } from '../util/zod';
import { Rights, rightsSchema } from './common';
import { formatExtensionSchema } from './extensions';
import { ImageSize, imageSizeSchema, jFileLittleSchema } from './file';
import { Audience, audienceSchema, jFolderLittleSchema } from './object';
import { jObjectLittle, jObjectLittleSchema } from './objectBase';
import { jUserLittle, jUserLittleSchema } from './user';
import { filtersSchema, formatListSchema, ordersSchema } from './utils';
import { WidgetWrapperProps, widgetWrapperPropsSchema } from './widgets/default';

export type jArticleLittle = jObjectLittle & {
    dateCreation: string;
    dateModified: string;
};
const jArticleLittleShapeTmp = jObjectLittleSchema.extend({
    dateCreation: z.string(),
    dateModified: z.string(),
});

export const jArticleLittleSchema = zSchema<jArticleLittle, typeof jArticleLittleShapeTmp.shape>(
    jArticleLittleShapeTmp,
);

export type jArticleList = jArticleLittle & {
    description?: string | null | undefined;
    _user: jUserLittle;
    _right: Rights;
    _audience: Audience[];
    image?: ImageSize | undefined;
    articleWidgets?: WidgetWrapperProps[] | undefined;
};

export const jArticleListShapeTmp = jArticleLittleSchema.extend({
    description: z.string().nullable().optional(),
    _user: jUserLittleSchema,
    _right: rightsSchema,
    _audience: audienceSchema.array(),
    image: imageSizeSchema.optional(),
    articleWidgets: widgetWrapperPropsSchema.array().optional(),
});
export const jArticleListSchema = zSchema<jArticleList, typeof jArticleListShapeTmp.shape>(jArticleListShapeTmp);

export const jArticleViewSchema = jArticleListSchema.extend({
    _attachedFiles: jFileLittleSchema.array(),
});
export type jArticleView = z.infer<typeof jArticleViewSchema>;

export const jArticleExtendViewSchema = jArticleViewSchema.extend({
    processContext: z.null(),
    _transientCtx: z.null(),
    uiObjectLink: jObjectLittleSchema.array().optional(),
});
export type jArticleExtendView = z.infer<typeof jArticleExtendViewSchema>;

export const inferJInternalDraftView = <T extends ZodType>(type: T) =>
    jArticleViewSchema.extend({
        idDraft: z.number(),
        form: type,
    });
export type jInternalDraftView<T> = z.infer<ReturnType<typeof inferJInternalDraftView<ZodType<T>>>>;

export const articleListConfigSchema = z.object({
    type: z.string().optional(),
    format: formatListSchema.optional(),
    formatExtend: formatExtensionSchema.optional(),
    query: z.string().optional(),
    limit: z.number().optional(),
    page: z.number().optional(),
    orders: ordersSchema.optional(),
    filters: filtersSchema.optional(),
    public: z.boolean().optional(),
});
export type ArticleListConfig = z.infer<typeof articleListConfigSchema>;

export const articleGetConfigSchema = z.object({
    uri: z.string().optional(),
    idArticle: z.number().optional(),
    format: formatListSchema.optional(),
});
export type ArticleGetConfig = z.infer<typeof articleGetConfigSchema>;

export const articleCreationSchema = z.object({
    title: z.string().optional(),
    description: z.string().optional().nullable(),
    type: z.string().optional(),
    publishTo: z.string().optional(),
    idSpot: z.array(z.string()).optional(),
});
export type ArticleCreation = z.infer<typeof articleCreationSchema>;

export const baseArticleSchema = z.object({
    id: z.number(),
    type: z.string(),
    dateCreation: z.string(),
    dateModified: z.string(),
    title: z.string(),
    uri: z.string(),
});
export type BaseArticle = z.infer<typeof baseArticleSchema>;

export const getCommentsParamsSchema = z.object({
    attachment: z.boolean().optional(),
    idArticle: z.number(),
    limits: z
        .object({
            max: z.number(),
            start: z.number(),
        })
        .optional(),
});
export type GetCommentsParams = z.infer<typeof getCommentsParamsSchema>;

export const addCommentParamsSchema = z.object({
    token: z.string(),
    content: z.string(),
    idArticle: z.number(),
    sendAlert: z.boolean(),
    alertAuthor: z.boolean(),
    widgets: widgetWrapperPropsSchema.array().optional(),
    type: z.union([z.literal('comment'), z.literal('commentResponse')]).optional(),
});
export type AddCommentParams = z.infer<typeof addCommentParamsSchema>;

export const updateCommentParamsSchema = z.object({
    token: z.string().optional(),
    description: z.string().optional(),
    idComment: z.number(),
    widgets: widgetWrapperPropsSchema.array().optional(),
});
export type UpdateCommentParams = z.infer<typeof updateCommentParamsSchema>;

export const userReadSchema = z.object({
    idUser: z.string(),
    dateRead: z.string(),
    Pseudo: z.string(),
    type: z.string(),
});
export type UserRead = z.infer<typeof userReadSchema>;

export const jFolderListSchema = jFolderLittleSchema.merge(jArticleListSchema).extend({
    _nbItems: z.number(),
    parentURI: z.string().optional(),
});
export type jFolderList = z.infer<typeof jFolderListSchema>;
